import { OperatorElearningTopicContentTranslationService } from '../../../../../api/api-services/operator-elearning-topic-content-translation.service';
import { IOperatorElearningTopicContentTranslationUm } from '../../../../../api/operator-elearning-topic-content-translation-um.model';
import { IOperatorElearningTopicContentTranslationIm } from '../../../../../api/operator-elearning-topic-content-translation-im.model';
import { OperatorElearningTopicContentService } from '../../../../../api/api-services/operator-elearning-topic-content.service';
import { OperatorElearningTopicsService } from '../../../../../api/api-services/operator-elearning-topics.service';
import { OperatorElearningTopicCategoriesService } from '../../../../../api/api-services/operator-elearning-topic-categories.service';
import { IOperatorElearningTopicCategoryVm } from '../../../../../api/operator-elearning-topic-category-vm.model';
import { IExamViewModel } from '../../../../../api/exam-view-model.model';
import { IOperatorElearningTopicContentVm } from '../../../../../api/operator-elearning-topic-content-vm.model';
import { IOperatorElearningTopicContentUm } from '../../../../../api/operator-elearning-topic-content-um.model';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  OnChanges,
} from '@angular/core';
import { IOperatorElearningTopicVm } from '../../../../../api/operator-elearning-topic-vm.model';
import { IDetailsControl } from '../../../../shared/models/details-control';
import { environment } from '../../../../../environments/environment';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

interface UploadFileResultVm {
  publicVisible: boolean;
  success: boolean;
  size: number;
  fileUrl: string;
}

@Component({
  selector: 'app-content-details',
  templateUrl: './content-details.component.html',
  styleUrl: './content-details.component.scss',
})
export class ContentDetailsComponent implements OnInit, OnChanges {
  @Input() topic!: IOperatorElearningTopicContentVm;
  @Input() topicList!: IOperatorElearningTopicContentVm[];
  @Input() topicIndex!: number;
  @Input() detailsControl: IDetailsControl = {
    isEditing: false,
    isOpen: false,
  };

  @Output() detailsControlChange = new EventEmitter<IDetailsControl>();
  @Output() refreshTopics = new EventEmitter<void>();

  private _operatorElearningTopicContentTranslationService = inject(
    OperatorElearningTopicContentTranslationService
  );
  private _operatorElearningTopicContentService = inject(
    OperatorElearningTopicContentService
  );
  private _operatorElearningTopicsService = inject(
    OperatorElearningTopicsService
  );
  private _operatorElearningTopicCategoriesService = inject(
    OperatorElearningTopicCategoriesService
  );
  private _messageService = inject(MessageService);
  private _http = inject(HttpClient);
  private _fb = inject(FormBuilder);

  public availableTopics: IOperatorElearningTopicVm[] = [];
  public availableCategories: IOperatorElearningTopicCategoryVm[] = [];

  public environment = environment;
  public isLoading = false;
  public isSaving = false;

  private skipFormReinitOnEditToggle = false;

  public editingImageLink = false;
  public savingImageLink = false;
  public editingMediaLink = false;
  public savingMediaLink = false;
  public editingAudioLink = false;
  public savingAudioLink = false;

  public editingTranslationSlideContent: { [key: string]: boolean } = {};
  public savingTranslationSlideContent: { [key: string]: boolean } = {};
  public editingTranslationSlideTitle: { [key: string]: boolean } = {};
  public savingTranslationSlideTitle: { [key: string]: boolean } = {};
  public editingTranslationImageLink: { [key: string]: boolean } = {};
  public savingTranslationImageLink: { [key: string]: boolean } = {};
  public editingTranslationMediaLink: { [key: string]: boolean } = {};
  public savingTranslationMediaLink: { [key: string]: boolean } = {};
  public editingTranslationAudioLink: { [key: string]: boolean } = {};
  public savingTranslationAudioLink: { [key: string]: boolean } = {};

  public selectedTranslationImageFiles: {
    [languageCode: string]: File | null;
  } = {};
  public selectedTranslationMediaFiles: {
    [languageCode: string]: File | null;
  } = {};
  public selectedTranslationAudioFiles: {
    [languageCode: string]: File | null;
  } = {};
  public selectedImageFile: File | null = null;
  public selectedMediaFile: File | null = null;
  public selectedAudioFile: File | null = null;

  public availableLanguages = [
    { code: 'en', name: 'Angielski', isDefault: false },
    { code: 'uk', name: 'Ukraiński', isDefault: false },
    { code: 'de', name: 'Niemiecki', isDefault: false },
  ];

  public contentForm!: FormGroup;
  public translationSlideContentForms: { [key: string]: FormGroup } = {};
  public translationSlideTitleForms: { [key: string]: FormGroup } = {};
  public translationForms: { [key: string]: FormGroup } = {};
  public editingTranslation: { [key: string]: boolean } = {};
  public savingTranslation: { [key: string]: boolean } = {};

  ngOnInit(): void {
    this.loadAvailableTopics();
    this.loadAvailableCategories();
    this.initializeForms();
  }

  ngOnChanges(): void {
    if (this.topic && this.detailsControl.isOpen) {
      this.initializeForms();
    }
  }

  private loadAvailableTopics(): void {
    this._operatorElearningTopicsService.get().subscribe({
      next: (topics) => {
        this.availableTopics = topics;
      },
      error: (error) => {
        this.availableTopics = [];
      },
    });
  }

  private loadAvailableCategories(): void {
    this._operatorElearningTopicCategoriesService.get().subscribe({
      next: (categories) => {
        this.availableCategories = categories;
      },
      error: (error) => {
        this.availableCategories = [];
      },
    });
  }

  public getExamCategoryNames(): string {
    return (this.topic.examCategories || []).map((e) => e.examName).join(', ');
  }

  public get availableExamsForCurrentTopic(): IExamViewModel[] {
    const topicId = this.contentForm?.get('elearningTopicId')?.value;
    const topic = this.availableTopics.find((t) => t.id === topicId);
    if (!topic) {
      return [];
    }
    const category = this.availableCategories.find(
      (c) => c.id === topic.elearningTopicCategoryId
    );
    return category?.examCategories || [];
  }

  private initializeForms(): void {
    if (!this.topic) return;

    this.contentForm = this._fb.group({
      id: [this.topic.id],
      slideTitle: [this.topic.slideTitle],
      slideContent: [this.topic.slideContent || '', Validators.required],
      imageLink: [this.topic.imageLink || ''],
      mediaLink: [this.topic.mediaLink || ''],
      audioLink: [this.topic.audioLink || ''],
      slideOrder: [this.topic.slideOrder || 0, [Validators.min(0)]],
      elearningTopicId: [this.topic.elearningTopicId],
      examsGuid: [this.topic.examCategories?.map((e) => e.guid) || []],
    });

    this.translationForms = {};
    this.translationSlideTitleForms = {};
    this.translationSlideContentForms = {};
    this.editingTranslation = {};
    this.savingTranslation = {};
    this.editingTranslationSlideTitle = {};
    this.savingTranslationSlideTitle = {};
    this.editingTranslationSlideContent = {};
    this.savingTranslationSlideContent = {};
    this.editingTranslationImageLink = {};
    this.savingTranslationImageLink = {};
    this.editingTranslationMediaLink = {};
    this.savingTranslationMediaLink = {};
    this.editingTranslationAudioLink = {};
    this.savingTranslationAudioLink = {};

    this.availableLanguages.forEach((lang) => {
      if (lang.isDefault) {
        return;
      }

      const existingTranslation =
        this.topic.elearningTopicContentTranslation?.find(
          (t: any) => t.languageCode === lang.code
        );

      this.translationForms[lang.code] = this._fb.group({
        id: [existingTranslation?.id || 0],
        languageCode: [lang.code, Validators.required],
        elearningTopicContentId: [this.topic.id, Validators.required],
        slideTitleOverride: [existingTranslation?.slideTitleOverride || ''],
        slideContentOverride: [existingTranslation?.slideContentOverride || ''],
        imageLinkOverride: [existingTranslation?.imageLinkOverride || ''],
        mediaLinkOverride: [existingTranslation?.mediaLinkOverride || ''],
        audioLinkOverride: [existingTranslation?.audioLinkOverride || ''],
      });

      this.translationSlideTitleForms[lang.code] = this._fb.group({
        slideTitleOverride: [existingTranslation?.slideTitleOverride || ''],
      });

      this.translationSlideContentForms[lang.code] = this._fb.group({
        slideContentOverride: [existingTranslation?.slideContentOverride || ''],
      });

      this.editingTranslation[lang.code] = false;
      this.savingTranslation[lang.code] = false;
      this.editingTranslationSlideTitle[lang.code] = false;
      this.savingTranslationSlideTitle[lang.code] = false;
      this.editingTranslationSlideContent[lang.code] = false;
      this.savingTranslationSlideContent[lang.code] = false;
      this.editingTranslationImageLink[lang.code] = false;
      this.savingTranslationImageLink[lang.code] = false;
      this.editingTranslationMediaLink[lang.code] = false;
      this.savingTranslationMediaLink[lang.code] = false;
      this.editingTranslationAudioLink[lang.code] = false;
      this.savingTranslationAudioLink[lang.code] = false;
    });
  }

  public closeDetails(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
    this.detailsControlChange.emit(this.detailsControl);
    this.resetEditingStates();
  }

  public editToggle(): void {
    if (!this.detailsControl.isEditing && this.isAnyTranslationBeingEdited()) {
      return;
    }

    this.detailsControl.isEditing = !this.detailsControl.isEditing;
    this.detailsControlChange.emit(this.detailsControl);

    if (!this.detailsControl.isEditing) {
      if (this.skipFormReinitOnEditToggle) {
        this.skipFormReinitOnEditToggle = false;
        return;
      }
      this.initializeForms();
    }
  }

  public saveContent(): void {
    if (this.contentForm.invalid) {
      this.markFormGroupTouched(this.contentForm);
      return;
    }

    this.isSaving = true;
    const formValue = this.contentForm.value;

    const originalTopic = { ...this.topic };

    const updateData: IOperatorElearningTopicContentUm = {
      id: this.topic.id,
      slideTitle: formValue.slideTitle,
      slideContent: formValue.slideContent,
      imageLink: this.topic.imageLink,
      mediaLink: this.topic.mediaLink,
      audioLink: this.topic.audioLink,
      elearningTopicId: formValue.elearningTopicId,
      slideOrder: formValue.slideOrder,
      examsGuid: formValue.examsGuid || [],
    };

    this.topic = {
      ...this.topic,
      slideTitle: formValue.slideTitle,
      slideContent: formValue.slideContent,
      elearningTopicId: formValue.elearningTopicId,
      slideOrder: formValue.slideOrder,
    };

    if (formValue.elearningTopicId !== originalTopic.elearningTopicId) {
      const selectedTopic = this.availableTopics.find(
        (t) => t.id === formValue.elearningTopicId
      );
      if (selectedTopic) {
        this.topic.elearningTopicName = selectedTopic.name;
      } else if (!formValue.elearningTopicId) {
        this.topic.elearningTopicName = '';
      }
    }

    this._operatorElearningTopicContentService.put(updateData).subscribe({
      next: (response: IOperatorElearningTopicContentVm) => {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Dane zostały zaktualizowane',
        });

        this.topic = {
          ...this.topic,
          examCategories: response.examCategories,
        };
        if (this.topicList && this.topicIndex >= 0) {
          this.topicList[this.topicIndex] = this.topic;
        }

        this.skipFormReinitOnEditToggle = true;
        this.detailsControl.isEditing = false;
        this.detailsControlChange.emit(this.detailsControl);
        this.refreshTopics.emit();
        this.isSaving = false;
      },
      error: (error: any) => {
        this.topic = originalTopic;
        if (this.topicList && this.topicIndex >= 0) {
          this.topicList[this.topicIndex] = originalTopic;
        }

        this.initializeForms();

        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się zaktualizować danych',
        });
        this.isSaving = false;
      },
    });
  }

  public enableImageLinkEditing(): void {
    if (this.detailsControl.isEditing || this.isAnyTranslationBeingEdited()) {
      return;
    }
    this.editingImageLink = true;
  }

  public cancelImageLinkEditing(): void {
    this.editingImageLink = false;
    this.selectedImageFile = null;
  }

  public enableMediaLinkEditing(): void {
    if (this.detailsControl.isEditing || this.isAnyTranslationBeingEdited()) {
      return;
    }
    this.editingMediaLink = true;
  }

  public cancelMediaLinkEditing(): void {
    this.editingMediaLink = false;
    this.selectedMediaFile = null;
  }

  public enableAudioLinkEditing(): void {
    if (this.detailsControl.isEditing || this.isAnyTranslationBeingEdited()) {
      return;
    }
    this.editingAudioLink = true;
  }

  public cancelAudioLinkEditing(): void {
    this.editingAudioLink = false;
    this.selectedAudioFile = null;
  }

  public hideDialog(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
    this.resetEditingStates();
    this.detailsControlChange.emit(this.detailsControl);
  }

  public enableTranslationEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail:
          'Zakończ edycję głównego formularza lub innego tłumaczenia przed rozpoczęciem edycji tego tłumaczenia',
      });
      return;
    }
    this.editingTranslation[languageCode] = true;
  }

  public cancelTranslationEditing(languageCode: string): void {
    this.editingTranslation[languageCode] = false;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (existingTranslation) {
      this.translationForms[languageCode].patchValue({
        slideTitleOverride: existingTranslation.slideTitleOverride,
        slideContentOverride: existingTranslation.slideContentOverride,
        imageLinkOverride: existingTranslation.imageLinkOverride,
        mediaLinkOverride: existingTranslation.mediaLinkOverride,
        audioLinkOverride: existingTranslation.audioLinkOverride,
      });
    } else {
      this.translationForms[languageCode].patchValue({
        slideTitleOverride: '',
        slideContentOverride: '',
        imageLinkOverride: '',
        mediaLinkOverride: '',
        audioLinkOverride: '',
      });
    }

    this.selectedTranslationImageFiles[languageCode] = null;
    this.selectedTranslationMediaFiles[languageCode] = null;
    this.selectedTranslationAudioFiles[languageCode] = null;
  }

  public async saveTranslation(languageCode: string): Promise<void> {
    const form = this.translationForms[languageCode];
    if (form.invalid) {
      this.markFormGroupTouched(form);
      return;
    }

    this.savingTranslation[languageCode] = true;
    const formValue = form.value;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    try {
      let imageLinkOverride = formValue.imageLinkOverride;
      let mediaLinkOverride = formValue.mediaLinkOverride;
      let audioLinkOverride = formValue.audioLinkOverride;

      if (this.selectedTranslationImageFiles[languageCode]) {
        imageLinkOverride = await this.uploadImageToServer(
          this.selectedTranslationImageFiles[languageCode]!
        );
      }

      if (this.selectedTranslationMediaFiles[languageCode]) {
        mediaLinkOverride = await this.uploadFileToServer(
          this.selectedTranslationMediaFiles[languageCode]!
        );
      }

      if (this.selectedTranslationAudioFiles[languageCode]) {
        audioLinkOverride = await this.uploadFileToServer(
          this.selectedTranslationAudioFiles[languageCode]!
        );
      }

      if (
        existingTranslation &&
        existingTranslation.id &&
        existingTranslation.id > 0
      ) {
        const updateData: IOperatorElearningTopicContentTranslationUm = {
          id: existingTranslation.id,
          slideTitleOverride: formValue.slideTitleOverride,
          slideContentOverride: formValue.slideContentOverride,
          imageLinkOverride: imageLinkOverride,
          mediaLinkOverride: mediaLinkOverride,
          audioLinkOverride: audioLinkOverride,
        };

        this._operatorElearningTopicContentTranslationService
          .put(updateData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationSaveSuccess(
                languageCode,
                response,
                'zaktualizowane'
              );
              this.clearTranslationFiles(languageCode);
            },
            error: (error: any) => {
              this.handleTranslationSaveError(
                languageCode,
                error,
                'zaktualizować'
              );
            },
          });
      } else {
        const newTranslation: IOperatorElearningTopicContentTranslationIm = {
          elearningTopicContentId: formValue.elearningTopicContentId,
          slideTitleOverride: formValue.slideTitleOverride,
          slideContentOverride: formValue.slideContentOverride,
          imageLinkOverride: imageLinkOverride,
          mediaLinkOverride: mediaLinkOverride,
          audioLinkOverride: audioLinkOverride,
          languageCode: formValue.languageCode,
        };

        this._operatorElearningTopicContentTranslationService
          .post(newTranslation)
          .subscribe({
            next: (response: any) => {
              form.patchValue({ id: response.id });
              this.handleTranslationSaveSuccess(
                languageCode,
                response,
                'utworzone'
              );
              this.clearTranslationFiles(languageCode);
            },
            error: (error: any) => {
              this.handleTranslationSaveError(languageCode, error, 'utworzyć');
            },
          });
      }
    } catch (error) {
      this.savingTranslation[languageCode] = false;
    }
  }

  public deleteTranslation(languageCode: string): void {
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (!existingTranslation || existingTranslation.id <= 0) {
      this.translationForms[languageCode].patchValue({
        slideTitleOverride: '',
        slideContentOverride: '',
        imageLinkOverride: '',
        mediaLinkOverride: '',
        audioLinkOverride: '',
      });
      return;
    }

    this.savingTranslation[languageCode] = true;

    this._operatorElearningTopicContentTranslationService
      .delete(existingTranslation.id)
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: `Tłumaczenie zostało usunięte`,
          });

          if (this.topic.elearningTopicContentTranslation) {
            this.topic.elearningTopicContentTranslation =
              this.topic.elearningTopicContentTranslation.filter(
                (t: any) => t.languageCode !== languageCode
              );
          }

          this.translationForms[languageCode].patchValue({
            id: 0,
            slideTitleOverride: '',
            slideContentOverride: '',
            imageLinkOverride: '',
            mediaLinkOverride: '',
            audioLinkOverride: '',
          });

          this.savingTranslation[languageCode] = false;
          this.editingTranslation[languageCode] = false;
          this.refreshTopics.emit();
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się usunąć tłumaczenia',
          });
          this.savingTranslation[languageCode] = false;
        },
      });
  }

  public deleteTranslationSlideTitle(languageCode: string): void {
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (!existingTranslation || existingTranslation.id <= 0) {
      this.translationSlideTitleForms[languageCode].patchValue({
        slideTitleOverride: '',
      });
      return;
    }

    this.savingTranslationSlideTitle[languageCode] = true;

    const updateData: IOperatorElearningTopicContentTranslationUm = {
      id: existingTranslation.id,
      slideTitleOverride: '',
      slideContentOverride: existingTranslation.slideContentOverride || '',
      imageLinkOverride: existingTranslation.imageLinkOverride || '',
      mediaLinkOverride: existingTranslation.mediaLinkOverride || '',
      audioLinkOverride: existingTranslation.audioLinkOverride || '',
    };

    this._operatorElearningTopicContentTranslationService
      .put(updateData)
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Tytuł tłumaczenia został usunięty',
          });

          if (this.topic.elearningTopicContentTranslation) {
            const index = this.topic.elearningTopicContentTranslation.findIndex(
              (t: any) => t.languageCode === languageCode
            );
            if (index >= 0) {
              this.topic.elearningTopicContentTranslation[index] = {
                ...this.topic.elearningTopicContentTranslation[index],
                slideTitleOverride: '',
              };
            }
          }

          this.translationSlideTitleForms[languageCode].patchValue({
            slideTitleOverride: '',
          });

          this.savingTranslationSlideTitle[languageCode] = false;
          this.refreshTopics.emit();
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się usunąć tytułu tłumaczenia',
          });
          this.savingTranslationSlideTitle[languageCode] = false;
        },
      });
  }

  public deleteTranslationSlideContent(languageCode: string): void {
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (!existingTranslation || existingTranslation.id <= 0) {
      this.translationSlideContentForms[languageCode].patchValue({
        slideContentOverride: '',
      });
      return;
    }

    this.savingTranslationSlideContent[languageCode] = true;

    const updateData: IOperatorElearningTopicContentTranslationUm = {
      id: existingTranslation.id,
      slideTitleOverride: existingTranslation.slideTitleOverride || '',
      slideContentOverride: '',
      imageLinkOverride: existingTranslation.imageLinkOverride || '',
      mediaLinkOverride: existingTranslation.mediaLinkOverride || '',
      audioLinkOverride: existingTranslation.audioLinkOverride || '',
    };

    this._operatorElearningTopicContentTranslationService
      .put(updateData)
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Treść tłumaczenia została usunięta',
          });

          if (this.topic.elearningTopicContentTranslation) {
            const index = this.topic.elearningTopicContentTranslation.findIndex(
              (t: any) => t.languageCode === languageCode
            );
            if (index >= 0) {
              this.topic.elearningTopicContentTranslation[index] = {
                ...this.topic.elearningTopicContentTranslation[index],
                slideContentOverride: '',
              };
            }
          }

          this.translationSlideContentForms[languageCode].patchValue({
            slideContentOverride: '',
          });

          this.savingTranslationSlideContent[languageCode] = false;
          this.refreshTopics.emit();
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się usunąć treści tłumaczenia',
          });
          this.savingTranslationSlideContent[languageCode] = false;
        },
      });
  }

  public deleteTranslationImageLink(languageCode: string): void {
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (!existingTranslation || existingTranslation.id <= 0) {
      return;
    }

    this.savingTranslationImageLink[languageCode] = true;

    const updateData: IOperatorElearningTopicContentTranslationUm = {
      id: existingTranslation.id,
      slideTitleOverride: existingTranslation.slideTitleOverride || '',
      slideContentOverride: existingTranslation.slideContentOverride || '',
      imageLinkOverride: '',
      mediaLinkOverride: existingTranslation.mediaLinkOverride || '',
      audioLinkOverride: existingTranslation.audioLinkOverride || '',
    };

    this._operatorElearningTopicContentTranslationService
      .put(updateData)
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Obraz tłumaczenia został usunięty',
          });

          if (this.topic.elearningTopicContentTranslation) {
            const index = this.topic.elearningTopicContentTranslation.findIndex(
              (t: any) => t.languageCode === languageCode
            );
            if (index >= 0) {
              this.topic.elearningTopicContentTranslation[index] = {
                ...this.topic.elearningTopicContentTranslation[index],
                imageLinkOverride: '',
              };
            }
          }

          this.savingTranslationImageLink[languageCode] = false;
          this.refreshTopics.emit();
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się usunąć obrazu tłumaczenia',
          });
          this.savingTranslationImageLink[languageCode] = false;
        },
      });
  }

  public deleteTranslationMediaLink(languageCode: string): void {
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (!existingTranslation || existingTranslation.id <= 0) {
      return;
    }

    this.savingTranslationMediaLink[languageCode] = true;

    const updateData: IOperatorElearningTopicContentTranslationUm = {
      id: existingTranslation.id,
      slideTitleOverride: existingTranslation.slideTitleOverride || '',
      slideContentOverride: existingTranslation.slideContentOverride || '',
      imageLinkOverride: existingTranslation.imageLinkOverride || '',
      mediaLinkOverride: '',
      audioLinkOverride: existingTranslation.audioLinkOverride || '',
    };

    this._operatorElearningTopicContentTranslationService
      .put(updateData)
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Wideo tłumaczenia zostało usunięte',
          });

          if (this.topic.elearningTopicContentTranslation) {
            const index = this.topic.elearningTopicContentTranslation.findIndex(
              (t: any) => t.languageCode === languageCode
            );
            if (index >= 0) {
              this.topic.elearningTopicContentTranslation[index] = {
                ...this.topic.elearningTopicContentTranslation[index],
                mediaLinkOverride: '',
              };
            }
          }

          this.savingTranslationMediaLink[languageCode] = false;
          this.refreshTopics.emit();
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się usunąć wideo tłumaczenia',
          });
          this.savingTranslationMediaLink[languageCode] = false;
        },
      });
  }

  public deleteTranslationAudioLink(languageCode: string): void {
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (!existingTranslation || existingTranslation.id <= 0) {
      return;
    }

    this.savingTranslationAudioLink[languageCode] = true;

    const updateData: IOperatorElearningTopicContentTranslationUm = {
      id: existingTranslation.id,
      slideTitleOverride: existingTranslation.slideTitleOverride || '',
      slideContentOverride: existingTranslation.slideContentOverride || '',
      imageLinkOverride: existingTranslation.imageLinkOverride || '',
      mediaLinkOverride: existingTranslation.mediaLinkOverride || '',
      audioLinkOverride: '',
    };

    this._operatorElearningTopicContentTranslationService
      .put(updateData)
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Audio tłumaczenia zostało usunięte',
          });

          if (this.topic.elearningTopicContentTranslation) {
            const index = this.topic.elearningTopicContentTranslation.findIndex(
              (t: any) => t.languageCode === languageCode
            );
            if (index >= 0) {
              this.topic.elearningTopicContentTranslation[index] = {
                ...this.topic.elearningTopicContentTranslation[index],
                audioLinkOverride: '',
              };
            }
          }

          this.savingTranslationAudioLink[languageCode] = false;
          this.refreshTopics.emit();
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się usunąć audio tłumaczenia',
          });
          this.savingTranslationAudioLink[languageCode] = false;
        },
      });
  }

  public hasTranslation(languageCode: string): boolean {
    return (
      this.topic.elearningTopicContentTranslation?.some(
        (t: any) =>
          t.languageCode === languageCode &&
          (t.slideTitleOverride || t.slideContentOverride)
      ) || false
    );
  }

  public getTranslationSlideTitle(languageCode: string): string {
    const translation = this.topic.elearningTopicContentTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );
    return translation?.slideTitleOverride || '';
  }

  public enableTranslationSlideTitleEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      return;
    }
    this.editingTranslationSlideTitle[languageCode] = true;
  }

  public cancelTranslationSlideTitleEditing(languageCode: string): void {
    this.editingTranslationSlideTitle[languageCode] = false;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    this.translationSlideTitleForms[languageCode].patchValue({
      slideTitleOverride: existingTranslation?.slideTitleOverride || '',
    });
  }

  public saveTranslationSlideTitle(languageCode: string): void {
    const form = this.translationSlideTitleForms[languageCode];
    if (form.invalid) {
      this.markFormGroupTouched(form);
      return;
    }

    this.savingTranslationSlideTitle[languageCode] = true;
    const formValue = form.value;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (
      existingTranslation &&
      existingTranslation.id &&
      existingTranslation.id > 0
    ) {
      const updateData: IOperatorElearningTopicContentTranslationUm = {
        id: existingTranslation.id,
        slideTitleOverride: formValue.slideTitleOverride,
        slideContentOverride: existingTranslation.slideContentOverride || '',
        imageLinkOverride: existingTranslation.imageLinkOverride || '',
        mediaLinkOverride: existingTranslation.mediaLinkOverride || '',
        audioLinkOverride: existingTranslation.audioLinkOverride || '',
      };

      this._operatorElearningTopicContentTranslationService
        .put(updateData)
        .subscribe({
          next: (response: any) => {
            this.handleTranslationSlideTitleSaveSuccess(
              languageCode,
              response
            );
          },
          error: (error: any) => {
            this.handleTranslationSlideTitleSaveError(languageCode, error);
          },
        });
    } else {
      const createData: IOperatorElearningTopicContentTranslationIm = {
        slideTitleOverride: formValue.slideTitleOverride,
        slideContentOverride: existingTranslation?.slideContentOverride || '',
        imageLinkOverride: existingTranslation?.imageLinkOverride || '',
        mediaLinkOverride: existingTranslation?.mediaLinkOverride || '',
        audioLinkOverride: existingTranslation?.audioLinkOverride || '',
        languageCode: languageCode,
        elearningTopicContentId: this.topic.id,
      };

      this._operatorElearningTopicContentTranslationService
        .post(createData)
        .subscribe({
          next: (response: any) => {
            this.handleTranslationSlideTitleSaveSuccess(
              languageCode,
              response
            );
          },
          error: (error: any) => {
            this.handleTranslationSlideTitleSaveError(languageCode, error);
          },
        });
    }
  }

  public getTranslationSlideContent(languageCode: string): string {
    const translation = this.topic.elearningTopicContentTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );
    return translation?.slideContentOverride || '';
  }

  public getTranslationImageLink(languageCode: string): string {
    const translation = this.topic.elearningTopicContentTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );
    return translation?.imageLinkOverride || '';
  }

  public getTranslationMediaLink(languageCode: string): string {
    const translation = this.topic.elearningTopicContentTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );
    return translation?.mediaLinkOverride || '';
  }

  public getTranslationAudioLink(languageCode: string): string {
    const translation = this.topic.elearningTopicContentTranslation?.find(
      (t: any) => t.languageCode === languageCode
    );
    return translation?.audioLinkOverride || '';
  }

  public onImageFileSelected(files: File | File[]): void {
    if (files instanceof File) {
      this.selectedImageFile = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name}`,
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedImageFile = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files[0].name}`,
      });
    }
  }

  public onMediaFileSelected(files: File | File[]): void {
    if (files instanceof File) {
      this.selectedMediaFile = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name}`,
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedMediaFile = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files[0].name}`,
      });
    }
  }

  public onAudioFileSelected(files: File | File[]): void {
    if (files instanceof File) {
      this.selectedAudioFile = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name}`,
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedAudioFile = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files[0].name}`,
      });
    }
  }

  public onTranslationImageSelected(
    files: File | File[],
    languageCode: string
  ): void {
    if (files instanceof File) {
      this.selectedTranslationImageFiles[languageCode] = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name} dla języka ${this.getLanguageName(
          languageCode
        )}`,
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedTranslationImageFiles[languageCode] = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${
          files[0].name
        } dla języka ${this.getLanguageName(languageCode)}`,
      });
    }
  }

  public onTranslationMediaSelected(
    files: File | File[],
    languageCode: string
  ): void {
    if (files instanceof File) {
      this.selectedTranslationMediaFiles[languageCode] = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name} dla języka ${this.getLanguageName(
          languageCode
        )}`,
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedTranslationMediaFiles[languageCode] = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${
          files[0].name
        } dla języka ${this.getLanguageName(languageCode)}`,
      });
    }
  }

  public onTranslationAudioSelected(
    files: File | File[],
    languageCode: string
  ): void {
    if (files instanceof File) {
      this.selectedTranslationAudioFiles[languageCode] = files;
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${files.name} dla języka ${this.getLanguageName(
          languageCode
        )}`,
      });
    } else if (Array.isArray(files) && files.length > 0) {
      this.selectedTranslationAudioFiles[languageCode] = files[0];
      this._messageService.add({
        severity: 'info',
        summary: 'Plik wybrany',
        detail: `Wybrano plik: ${
          files[0].name
        } dla języka ${this.getLanguageName(languageCode)}`,
      });
    }
  }

  public getLanguageName(languageCode: string): string {
    const language = this.availableLanguages.find(
      (lang) => lang.code === languageCode
    );
    return language?.name || languageCode;
  }

  public async saveImageLink(): Promise<void> {
    if (!this.selectedImageFile) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik obrazu',
      });
      return;
    }

    this.savingImageLink = true;

    const originalTopic = { ...this.topic };

    try {
      const uploadedUrl = await this.uploadImageToServer(
        this.selectedImageFile
      );

      const updateData: IOperatorElearningTopicContentUm = {
        id: this.topic.id,
        slideTitle: this.topic.slideTitle,
        slideContent: this.topic.slideContent,
        imageLink: uploadedUrl,
        mediaLink: this.topic.mediaLink,
        audioLink: this.topic.audioLink,
        slideOrder: this.topic.slideOrder,
        elearningTopicId: this.topic.elearningTopicId,
      };

      this.topic.imageLink = uploadedUrl;

      this._operatorElearningTopicContentService.put(updateData).subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Obraz został zaktualizowany',
          });

          this.editingImageLink = false;
          this.selectedImageFile = null;
          this.refreshTopics.emit();
          this.savingImageLink = false;
        },
        error: (error: any) => {
          this.topic = originalTopic;
          if (this.topicList && this.topicIndex >= 0) {
            this.topicList[this.topicIndex] = originalTopic;
          }

          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się zaktualizować obrazu',
          });
          this.savingImageLink = false;
        },
      });
    } catch (error) {
      this.savingImageLink = false;
    }
  }

  public async saveMediaLink(): Promise<void> {
    if (!this.selectedMediaFile) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik wideo',
      });
      return;
    }

    this.savingMediaLink = true;

    const originalTopic = { ...this.topic };

    try {
      const uploadedUrl = await this.uploadFileToServer(this.selectedMediaFile);

      const updateData: IOperatorElearningTopicContentUm = {
        id: this.topic.id,
        slideTitle: this.topic.slideTitle,
        slideContent: this.topic.slideContent,
        imageLink: this.topic.imageLink,
        mediaLink: uploadedUrl,
        audioLink: this.topic.audioLink,
        slideOrder: this.topic.slideOrder,
        elearningTopicId: this.topic.elearningTopicId,
      };

      this.topic.mediaLink = uploadedUrl;

      this._operatorElearningTopicContentService.put(updateData).subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Plik wideo został zaktualizowany',
          });

          this.editingMediaLink = false;
          this.selectedMediaFile = null;
          this.refreshTopics.emit();
          this.savingMediaLink = false;
        },
        error: (error: any) => {
          this.topic = originalTopic;
          if (this.topicList && this.topicIndex >= 0) {
            this.topicList[this.topicIndex] = originalTopic;
          }

          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się zaktualizować pliku wideo',
          });
          this.savingMediaLink = false;
        },
      });
    } catch (error) {
      this.savingMediaLink = false;
    }
  }

  public async saveAudioLink(): Promise<void> {
    if (!this.selectedAudioFile) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik audio',
      });
      return;
    }

    this.savingAudioLink = true;

    const originalTopic = { ...this.topic };

    try {
      const uploadedUrl = await this.uploadFileToServer(this.selectedAudioFile);

      const updateData: IOperatorElearningTopicContentUm = {
        id: this.topic.id,
        slideTitle: this.topic.slideTitle,
        slideContent: this.topic.slideContent,
        imageLink: this.topic.imageLink,
        mediaLink: this.topic.mediaLink,
        audioLink: uploadedUrl,
        slideOrder: this.topic.slideOrder,
        elearningTopicId: this.topic.elearningTopicId,
      };

      this.topic.audioLink = uploadedUrl;

      this._operatorElearningTopicContentService.put(updateData).subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Plik audio został zaktualizowany',
          });

          this.editingAudioLink = false;
          this.selectedAudioFile = null;
          this.refreshTopics.emit();
          this.savingAudioLink = false;
        },
        error: (error: any) => {
          this.topic = originalTopic;
          if (this.topicList && this.topicIndex >= 0) {
            this.topicList[this.topicIndex] = originalTopic;
          }

          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się zaktualizować pliku audio',
          });
          this.savingAudioLink = false;
        },
      });
    } catch (error) {
      this.savingAudioLink = false;
    }
  }

  public removeImageLink(): void {
    this.savingImageLink = true;

    const updateData: IOperatorElearningTopicContentUm = {
      id: this.topic.id,
      slideTitle: this.topic.slideTitle,
      slideContent: this.topic.slideContent,
      imageLink: '',
      mediaLink: this.topic.mediaLink,
      audioLink: this.topic.audioLink,
      slideOrder: this.topic.slideOrder,
      elearningTopicId: this.topic.elearningTopicId,
    };

    this._operatorElearningTopicContentService.put(updateData).subscribe({
      next: (response: any) => {
        this.topic.imageLink = '';

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Obraz został usunięty',
        });

        this.refreshTopics.emit();
        this.savingImageLink = false;
      },
      error: (error: any) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się usunąć obrazu',
        });
        this.savingImageLink = false;
      },
    });
  }

  public removeMediaLink(): void {
    this.savingMediaLink = true;

    const updateData: IOperatorElearningTopicContentUm = {
      id: this.topic.id,
      slideTitle: this.topic.slideTitle,
      slideContent: this.topic.slideContent,
      imageLink: this.topic.imageLink,
      mediaLink: '',
      audioLink: this.topic.audioLink,
      slideOrder: this.topic.slideOrder,
      elearningTopicId: this.topic.elearningTopicId,
    };

    this._operatorElearningTopicContentService.put(updateData).subscribe({
      next: (response: any) => {
        this.topic.mediaLink = '';

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Plik wideo został usunięty',
        });

        this.refreshTopics.emit();
        this.savingMediaLink = false;
      },
      error: (error: any) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się usunąć pliku wideo',
        });
        this.savingMediaLink = false;
      },
    });
  }

  public removeAudioLink(): void {
    this.savingAudioLink = true;

    const updateData: IOperatorElearningTopicContentUm = {
      id: this.topic.id,
      slideTitle: this.topic.slideTitle,
      slideContent: this.topic.slideContent,
      imageLink: this.topic.imageLink,
      mediaLink: this.topic.mediaLink,
      audioLink: '',
      slideOrder: this.topic.slideOrder,
      elearningTopicId: this.topic.elearningTopicId,
    };

    this._operatorElearningTopicContentService.put(updateData).subscribe({
      next: (response: any) => {
        this.topic.audioLink = '';

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Plik audio został usunięty',
        });

        this.refreshTopics.emit();
        this.savingAudioLink = false;
      },
      error: (error: any) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się usunąć pliku audio',
        });
        this.savingAudioLink = false;
      },
    });
  }

  private async uploadImageToServer(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('publicVisible', 'true');

      const response = await firstValueFrom(
        this._http.post<UploadFileResultVm>(
          `${environment.webApiUrl}/UploadMedia`,
          formData
        )
      );

      if (response.success) {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: `Zdjęcie zostało przesłane.`,
        });
        return response.fileUrl;
      } else {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się przesłać zdjęcia.',
        });
        throw new Error('Upload failed');
      }
    } catch (error) {
      this._messageService.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'Wystąpił błąd podczas przesyłania zdjęcia.',
      });
      throw error;
    }
  }

  private async uploadFileToServer(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('publicVisible', 'true');

      const response = await firstValueFrom(
        this._http.post<UploadFileResultVm>(
          `${environment.webApiUrl}/UploadMedia`,
          formData
        )
      );

      if (response.success) {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: `Plik zostało przesłany.`,
        });
        return response.fileUrl;
      } else {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się przesłać pliku.',
        });
        throw new Error('Upload failed');
      }
    } catch (error) {
      this._messageService.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'Wystąpił błąd podczas przesyłania pliku.',
      });
      throw error;
    }
  }

  private clearTranslationFiles(languageCode: string): void {
    this.selectedTranslationImageFiles[languageCode] = null;
    this.selectedTranslationMediaFiles[languageCode] = null;
    this.selectedTranslationAudioFiles[languageCode] = null;
  }

  private handleTranslationSaveSuccess(
    languageCode: string,
    translationData: any,
    action: string
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: `Tłumaczenie zostało ${action}`,
    });

    if (!this.topic.elearningTopicContentTranslation) {
      this.topic.elearningTopicContentTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicContentTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicContentTranslation[existingIndex] =
        translationData;
    } else {
      this.topic.elearningTopicContentTranslation.push(translationData);
    }

    this.editingTranslation[languageCode] = false;
    this.savingTranslation[languageCode] = false;
    this.refreshTopics.emit();
  }

  private handleTranslationSaveError(
    languageCode: string,
    error: any,
    action: string
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: `Nie udało się ${action} tłumaczenia`,
    });
    this.savingTranslation[languageCode] = false;
  }

  private resetEditingStates(): void {
    Object.keys(this.editingTranslation).forEach((lang) => {
      const language = this.availableLanguages.find((l) => l.code === lang);
      if (language?.isDefault) {
        return;
      }

      this.editingTranslation[lang] = false;
      this.savingTranslation[lang] = false;
      this.editingTranslationSlideTitle[lang] = false;
      this.savingTranslationSlideTitle[lang] = false;
      this.editingTranslationSlideContent[lang] = false;
      this.savingTranslationSlideContent[lang] = false;
      this.editingTranslationImageLink[lang] = false;
      this.savingTranslationImageLink[lang] = false;
      this.editingTranslationMediaLink[lang] = false;
      this.savingTranslationMediaLink[lang] = false;
      this.editingTranslationAudioLink[lang] = false;
      this.savingTranslationAudioLink[lang] = false;
    });
    this.editingImageLink = false;
    this.editingMediaLink = false;
    this.editingAudioLink = false;
    this.savingImageLink = false;
    this.savingMediaLink = false;
    this.savingAudioLink = false;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  private handleTranslationSlideTitleSaveSuccess(
    languageCode: string,
    translationData: any
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Tytuł tłumaczenia został zaktualizowany',
    });

    if (!this.topic.elearningTopicContentTranslation) {
      this.topic.elearningTopicContentTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicContentTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicContentTranslation[existingIndex] = {
        ...this.topic.elearningTopicContentTranslation[existingIndex],
        ...translationData
      };
    } else {
      this.topic.elearningTopicContentTranslation.push(translationData);
    }

    this.editingTranslationSlideTitle[languageCode] = false;
    this.savingTranslationSlideTitle[languageCode] = false;
    this.refreshTopics.emit();
  }

  private handleTranslationSlideTitleSaveError(
    languageCode: string,
    error: any
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zaktualizować tytułu tłumaczenia',
    });
    this.savingTranslationSlideTitle[languageCode] = false;
  }

  public enableTranslationSlideContentEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail:
          'Zakończ edycję głównego formularza lub innego tłumaczenia przed rozpoczęciem edycji tego tłumaczenia',
      });
      return;
    }
    this.editingTranslationSlideContent[languageCode] = true;
  }

  public cancelTranslationSlideContentEditing(languageCode: string): void {
    this.editingTranslationSlideContent[languageCode] = false;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    this.translationSlideContentForms[languageCode].patchValue({
      slideContentOverride: existingTranslation?.slideContentOverride || '',
    });
  }

  public saveTranslationSlideContent(languageCode: string): void {
    const form = this.translationSlideContentForms[languageCode];
    if (form.invalid) {
      this.markFormGroupTouched(form);
      return;
    }

    this.savingTranslationSlideContent[languageCode] = true;
    const formValue = form.value;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    if (
      existingTranslation &&
      existingTranslation.id &&
      existingTranslation.id > 0
    ) {
      const updateData: IOperatorElearningTopicContentTranslationUm = {
        id: existingTranslation.id,
        slideTitleOverride: existingTranslation.slideTitleOverride || '',
        slideContentOverride: formValue.slideContentOverride,
        imageLinkOverride: existingTranslation.imageLinkOverride || '',
        mediaLinkOverride: existingTranslation.mediaLinkOverride || '',
        audioLinkOverride: existingTranslation.audioLinkOverride || '',
      };

      this._operatorElearningTopicContentTranslationService
        .put(updateData)
        .subscribe({
          next: (response: any) => {
            this.handleTranslationSlideContentSaveSuccess(
              languageCode,
              response
            );
          },
          error: (error: any) => {
            this.handleTranslationSlideContentSaveError(languageCode, error);
          },
        });
    } else {
      const createData: IOperatorElearningTopicContentTranslationIm = {
        slideTitleOverride: existingTranslation?.slideTitleOverride || '',
        slideContentOverride: formValue.slideContentOverride,
        imageLinkOverride: existingTranslation?.imageLinkOverride || '',
        mediaLinkOverride: existingTranslation?.mediaLinkOverride || '',
        audioLinkOverride: existingTranslation?.audioLinkOverride || '',
        languageCode: languageCode,
        elearningTopicContentId: this.topic.id,
      };

      this._operatorElearningTopicContentTranslationService
        .post(createData)
        .subscribe({
          next: (response: any) => {
            this.handleTranslationSlideContentSaveSuccess(
              languageCode,
              response
            );
          },
          error: (error: any) => {
            this.handleTranslationSlideContentSaveError(languageCode, error);
          },
        });
    }
  }

  private handleTranslationSlideContentSaveSuccess(
    languageCode: string,
    translationData: any
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Treść tłumaczenia została zaktualizowana',
    });

    if (!this.topic.elearningTopicContentTranslation) {
      this.topic.elearningTopicContentTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicContentTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicContentTranslation[existingIndex] = {
        ...this.topic.elearningTopicContentTranslation[existingIndex],
        ...translationData
      };
    } else {
      this.topic.elearningTopicContentTranslation.push(translationData);
    }

    this.editingTranslationSlideContent[languageCode] = false;
    this.savingTranslationSlideContent[languageCode] = false;
    this.refreshTopics.emit();
  }

  private handleTranslationSlideContentSaveError(
    languageCode: string,
    error: any
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zaktualizować treści tłumaczenia',
    });
    this.savingTranslationSlideContent[languageCode] = false;
  }

  public enableTranslationImageLinkEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      return;
    }
    this.editingTranslationImageLink[languageCode] = true;
  }

  public cancelTranslationImageLinkEditing(languageCode: string): void {
    this.editingTranslationImageLink[languageCode] = false;
    this.selectedTranslationImageFiles[languageCode] = null;
  }

  public async saveTranslationImageLink(languageCode: string): Promise<void> {
    if (!this.selectedTranslationImageFiles[languageCode]) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik zdjęcia',
      });
      return;
    }

    this.savingTranslationImageLink[languageCode] = true;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    try {
      const uploadedUrl = await this.uploadImageToServer(
        this.selectedTranslationImageFiles[languageCode]!
      );

      if (
        existingTranslation &&
        existingTranslation.id &&
        existingTranslation.id > 0
      ) {
        const updateData: IOperatorElearningTopicContentTranslationUm = {
          id: existingTranslation.id,
          slideTitleOverride: existingTranslation.slideTitleOverride || '',
          slideContentOverride: existingTranslation.slideContentOverride || '',
          imageLinkOverride: uploadedUrl,
          mediaLinkOverride: existingTranslation.mediaLinkOverride || '',
          audioLinkOverride: existingTranslation.audioLinkOverride || '',
        };

        this._operatorElearningTopicContentTranslationService
          .put(updateData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationImageLinkSaveSuccess(
                languageCode,
                response
              );
            },
            error: (error: any) => {
              this.handleTranslationImageLinkSaveError(languageCode, error);
            },
          });
      } else {
        const createData: IOperatorElearningTopicContentTranslationIm = {
          slideTitleOverride: existingTranslation?.slideTitleOverride || '',
          slideContentOverride: existingTranslation?.slideContentOverride || '',
          imageLinkOverride: uploadedUrl,
          mediaLinkOverride: existingTranslation?.mediaLinkOverride || '',
          audioLinkOverride: existingTranslation?.audioLinkOverride || '',
          languageCode: languageCode,
          elearningTopicContentId: this.topic.id,
        };

        this._operatorElearningTopicContentTranslationService
          .post(createData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationImageLinkSaveSuccess(
                languageCode,
                response
              );
            },
            error: (error: any) => {
              this.handleTranslationImageLinkSaveError(languageCode, error);
            },
          });
      }
    } catch (error) {
      this.savingTranslationImageLink[languageCode] = false;
    }
  }

  private handleTranslationImageLinkSaveSuccess(
    languageCode: string,
    translationData: any
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Obraz tłumaczenia został zaktualizowany',
    });

    if (!this.topic.elearningTopicContentTranslation) {
      this.topic.elearningTopicContentTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicContentTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicContentTranslation[existingIndex] = {
        ...this.topic.elearningTopicContentTranslation[existingIndex],
        ...translationData
      };
    } else {
      this.topic.elearningTopicContentTranslation.push(translationData);
    }

    this.editingTranslationImageLink[languageCode] = false;
    this.savingTranslationImageLink[languageCode] = false;
    this.selectedTranslationImageFiles[languageCode] = null;
    this.refreshTopics.emit();
  }

  private handleTranslationImageLinkSaveError(
    languageCode: string,
    error: any
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zaktualizować obrazu tłumaczenia',
    });
    this.savingTranslationImageLink[languageCode] = false;
  }

  public enableTranslationMediaLinkEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      return;
    }
    this.editingTranslationMediaLink[languageCode] = true;
  }

  public cancelTranslationMediaLinkEditing(languageCode: string): void {
    this.editingTranslationMediaLink[languageCode] = false;
    this.selectedTranslationMediaFiles[languageCode] = null;
  }

  public async saveTranslationMediaLink(languageCode: string): Promise<void> {
    if (!this.selectedTranslationMediaFiles[languageCode]) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik wideo',
      });
      return;
    }

    this.savingTranslationMediaLink[languageCode] = true;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    try {
      const uploadedUrl = await this.uploadImageToServer(
        this.selectedTranslationMediaFiles[languageCode]!
      );

      if (
        existingTranslation &&
        existingTranslation.id &&
        existingTranslation.id > 0
      ) {
        const updateData: IOperatorElearningTopicContentTranslationUm = {
          id: existingTranslation.id,
          slideTitleOverride: existingTranslation.slideTitleOverride || '',
          slideContentOverride: existingTranslation.slideContentOverride || '',
          imageLinkOverride: existingTranslation.imageLinkOverride || '',
          mediaLinkOverride: uploadedUrl,
          audioLinkOverride: existingTranslation.audioLinkOverride || '',
        };

        this._operatorElearningTopicContentTranslationService
          .put(updateData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationMediaLinkSaveSuccess(
                languageCode,
                response
              );
            },
            error: (error: any) => {
              this.handleTranslationMediaLinkSaveError(languageCode, error);
            },
          });
      } else {
        const createData: IOperatorElearningTopicContentTranslationIm = {
          slideTitleOverride: existingTranslation?.slideTitleOverride || '',
          slideContentOverride: existingTranslation?.slideContentOverride || '',
          imageLinkOverride: existingTranslation?.imageLinkOverride || '',
          mediaLinkOverride: uploadedUrl,
          audioLinkOverride: existingTranslation?.audioLinkOverride || '',
          languageCode: languageCode,
          elearningTopicContentId: this.topic.id,
        };

        this._operatorElearningTopicContentTranslationService
          .post(createData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationMediaLinkSaveSuccess(
                languageCode,
                response
              );
            },
            error: (error: any) => {
              this.handleTranslationMediaLinkSaveError(languageCode, error);
            },
          });
      }
    } catch (error) {
      this.savingTranslationMediaLink[languageCode] = false;
    }
  }

  private handleTranslationMediaLinkSaveSuccess(
    languageCode: string,
    translationData: any
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Wideo tłumaczenia zostało zaktualizowane',
    });

    if (!this.topic.elearningTopicContentTranslation) {
      this.topic.elearningTopicContentTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicContentTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicContentTranslation[existingIndex] = {
        ...this.topic.elearningTopicContentTranslation[existingIndex],
        ...translationData
      };
    } else {
      this.topic.elearningTopicContentTranslation.push(translationData);
    }

    this.editingTranslationMediaLink[languageCode] = false;
    this.savingTranslationMediaLink[languageCode] = false;
    this.selectedTranslationMediaFiles[languageCode] = null;
    this.refreshTopics.emit();
  }

  private handleTranslationMediaLinkSaveError(
    languageCode: string,
    error: any
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zaktualizować wideo tłumaczenia',
    });
    this.savingTranslationMediaLink[languageCode] = false;
  }

  public enableTranslationAudioLinkEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      return;
    }
    this.editingTranslationAudioLink[languageCode] = true;
  }

  public cancelTranslationAudioLinkEditing(languageCode: string): void {
    this.editingTranslationAudioLink[languageCode] = false;
    this.selectedTranslationAudioFiles[languageCode] = null;
  }

  public async saveTranslationAudioLink(languageCode: string): Promise<void> {
    if (!this.selectedTranslationAudioFiles[languageCode]) {
      this._messageService.add({
        severity: 'warn',
        summary: 'Uwaga',
        detail: 'Proszę wybrać plik audio',
      });
      return;
    }

    this.savingTranslationAudioLink[languageCode] = true;
    const existingTranslation =
      this.topic.elearningTopicContentTranslation?.find(
        (t: any) => t.languageCode === languageCode
      );

    try {
      const uploadedUrl = await this.uploadImageToServer(
        this.selectedTranslationAudioFiles[languageCode]!
      );

      if (
        existingTranslation &&
        existingTranslation.id &&
        existingTranslation.id > 0
      ) {
        const updateData: IOperatorElearningTopicContentTranslationUm = {
          id: existingTranslation.id,
          slideTitleOverride: existingTranslation.slideTitleOverride || '',
          slideContentOverride: existingTranslation.slideContentOverride || '',
          imageLinkOverride: existingTranslation.imageLinkOverride || '',
          mediaLinkOverride: existingTranslation.mediaLinkOverride || '',
          audioLinkOverride: uploadedUrl,
        };

        this._operatorElearningTopicContentTranslationService
          .put(updateData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationAudioLinkSaveSuccess(
                languageCode,
                response
              );
            },
            error: (error: any) => {
              this.handleTranslationAudioLinkSaveError(languageCode, error);
            },
          });
      } else {
        const createData: IOperatorElearningTopicContentTranslationIm = {
          slideTitleOverride: existingTranslation?.slideTitleOverride || '',
          slideContentOverride: existingTranslation?.slideContentOverride || '',
          imageLinkOverride: existingTranslation?.imageLinkOverride || '',
          mediaLinkOverride: existingTranslation?.mediaLinkOverride || '',
          audioLinkOverride: uploadedUrl,
          languageCode: languageCode,
          elearningTopicContentId: this.topic.id,
        };

        this._operatorElearningTopicContentTranslationService
          .post(createData)
          .subscribe({
            next: (response: any) => {
              this.handleTranslationAudioLinkSaveSuccess(
                languageCode,
                response
              );
            },
            error: (error: any) => {
              this.handleTranslationAudioLinkSaveError(languageCode, error);
            },
          });
      }
    } catch (error) {
      this.savingTranslationAudioLink[languageCode] = false;
    }
  }

  private handleTranslationAudioLinkSaveSuccess(
    languageCode: string,
    translationData: any
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Audio tłumaczenia zostało zaktualizowane',
    });

    if (!this.topic.elearningTopicContentTranslation) {
      this.topic.elearningTopicContentTranslation = [];
    }

    const existingIndex = this.topic.elearningTopicContentTranslation.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.topic.elearningTopicContentTranslation[existingIndex] = {
        ...this.topic.elearningTopicContentTranslation[existingIndex],
        ...translationData
      };
    } else {
      this.topic.elearningTopicContentTranslation.push(translationData);
    }

    this.editingTranslationAudioLink[languageCode] = false;
    this.savingTranslationAudioLink[languageCode] = false;
    this.selectedTranslationAudioFiles[languageCode] = null;
    this.refreshTopics.emit();
  }

  private handleTranslationAudioLinkSaveError(
    languageCode: string,
    error: any
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zaktualizować audio tłumaczenia',
    });
    this.savingTranslationAudioLink[languageCode] = false;
  }

  public deleteTopicContent(): void {
    this._operatorElearningTopicContentService.delete(this.topic.id).subscribe({
      next: (res) => {
        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Slajd został usunięty',
        });
        this.hideDialog();
        this.refreshTopics.emit();
      },
      error: (error) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się usunąć slajdu',
        });
      },
    });
  }

  public isAnyTranslationBeingEdited(): boolean {
    const result =
      Object.values(this.editingTranslation).some((isEditing) => isEditing) ||
      Object.values(this.editingTranslationSlideTitle).some(
        (isEditing) => isEditing
      ) ||
      Object.values(this.editingTranslationSlideContent).some(
        (isEditing) => isEditing
      ) ||
      Object.values(this.editingTranslationImageLink).some(
        (isEditing) => isEditing
      ) ||
      Object.values(this.editingTranslationMediaLink).some(
        (isEditing) => isEditing
      ) ||
      Object.values(this.editingTranslationAudioLink).some(
        (isEditing) => isEditing
      ) ||
      this.editingImageLink ||
      this.editingMediaLink ||
      this.editingAudioLink;

    return result;
  }

  public shouldDisableButton(): boolean {
    const shouldDisable =
      this.detailsControl.isEditing || this.isAnyTranslationBeingEdited();
    return shouldDisable;
  }

  public canEditTranslation(languageCode: string): boolean {
    return (
      !this.detailsControl.isEditing &&
      !this.isAnyOtherTranslationBeingEdited(languageCode)
    );
  }

  public canEditTranslationField(
    languageCode: string,
    fieldType: 'title' | 'content' | 'image' | 'media' | 'audio' | 'general'
  ): boolean {
    if (this.detailsControl.isEditing) {
      return false;
    }

    if (this.isAnyOtherTranslationBeingEdited(languageCode)) {
      return false;
    }

    const currentlyEditing = {
      title: this.editingTranslationSlideTitle[languageCode],
      content: this.editingTranslationSlideContent[languageCode],
      image: this.editingTranslationImageLink[languageCode],
      media: this.editingTranslationMediaLink[languageCode],
      audio: this.editingTranslationAudioLink[languageCode],
      general: this.editingTranslation[languageCode],
    };

    delete currentlyEditing[fieldType];

    return !Object.values(currentlyEditing).some((isEditing) => isEditing);
  }

  private isAnyOtherTranslationBeingEdited(
    excludeLanguageCode: string
  ): boolean {
    return Object.keys(this.editingTranslation)
      .filter((lang) => lang !== excludeLanguageCode)
      .some(
        (lang) =>
          this.editingTranslation[lang] ||
          this.editingTranslationSlideTitle[lang] ||
          this.editingTranslationSlideContent[lang] ||
          this.editingTranslationImageLink[lang] ||
          this.editingTranslationMediaLink[lang] ||
          this.editingTranslationAudioLink[lang]
      );
  }
}
